import { useInfiniteQuery } from "@tanstack/react-query";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/shadcn/Button/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/shadcn/Dialog/Dialog";
import { aiChatGuardianReviewsOptions } from "@/lib/data/react-query/ai-chat";
import type { AiChatGuardianReview, ChatApi } from "@/lib/types/ai-chat";
import {
  ChatApprovalCard,
  type ChatApprovalCardProps,
} from "./ChatTimelineItem";

interface ChatPendingApprovalsProps {
  api: ChatApi;
  runId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  details: Readonly<Record<string, AiChatGuardianReview | undefined>>;
  decidingIds: ReadonlySet<string>;
  onLoadDetail: ChatApprovalCardProps["onLoadApprovalDetail"];
  onDecide: ChatApprovalCardProps["onDecideApproval"];
}

export function ChatPendingApprovals({
  api,
  runId,
  open,
  onOpenChange,
  details,
  decidingIds,
  onLoadDetail,
  onDecide,
}: ChatPendingApprovalsProps) {
  const query = useInfiniteQuery(
    aiChatGuardianReviewsOptions(
      open ? (runId ?? undefined) : undefined,
      "awaiting_human",
      api,
    ),
  );
  const reviews = query.data?.pages.flatMap((page) => page.reviews) ?? [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85svh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="size-5" />
            Review proposed actions
          </DialogTitle>
          <DialogDescription>
            Check the exact change before deciding whether to continue.
          </DialogDescription>
        </DialogHeader>
        {query.isLoading && (
          <p
            role="status"
            className="text-muted-foreground flex items-center gap-2 py-5 text-sm"
          >
            <LoaderCircle className="size-4 animate-spin" />
            Loading requests…
          </p>
        )}
        {query.error && (
          <div role="alert">
            <p className="text-destructive text-sm">{query.error.message}</p>
            <Button
              className="mt-3"
              variant="outline"
              onClick={() => {
                void query.refetch();
              }}
            >
              Try again
            </Button>
          </div>
        )}
        {!query.isLoading && !query.error && reviews.length === 0 && (
          <p className="text-muted-foreground py-5 text-sm">
            No actions are waiting for approval.
          </p>
        )}
        <div className="space-y-3">
          {reviews.map((review) => (
            <ChatApprovalCard
              key={review.id}
              review={review}
              detail={details[review.id]}
              isDecidingApproval={decidingIds.has(review.id)}
              onLoadApprovalDetail={onLoadDetail}
              onDecideApproval={onDecide}
            />
          ))}
        </div>
        {query.hasNextPage && (
          <Button
            variant="outline"
            disabled={query.isFetchingNextPage}
            onClick={() => {
              void query.fetchNextPage();
            }}
          >
            Load more requests
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
